import { AddCircleOutline, DeleteOutline } from '@mui/icons-material';
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useAppSelector } from 'app/hooks';
import {
  MeasurementData,
  measurementListWithExtras,
  MeasurementWithExtras,
  reverseWeatherMapWithExtras,
  weatherMapWithExtras,
} from 'features/weather/types';
import { getUnitForMeasurementType } from 'features/weather/utils/dataConverters';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { WeatherConstraint } from './types';
import { changeArrayAtIndex, removeElementAtIndex } from './utils/formHelpers';

interface Props {
  weatherConstraints: WeatherConstraint[];
  setWeatherConstraints: Dispatch<SetStateAction<WeatherConstraint[]>>;
  hide: boolean;
}

const WeatherReflectionConstraints = ({
  weatherConstraints,
  setWeatherConstraints,
  hide,
}: Props) => {
  const [unusedMeasurements, setUnusedMeasurements] = useState<
    MeasurementWithExtras[]
  >(measurementListWithExtras);
  const inImperial = useAppSelector(
    (state) => state.auth.user?.us_customary_units
  );
  useEffect(() => {
    const newUnusedMeasurements = measurementListWithExtras.filter(
      (measurementName) =>
        !weatherConstraints.some(
          (constraint) =>
            weatherMapWithExtras[measurementName].data ===
            constraint.measurement
        )
    );
    setUnusedMeasurements(newUnusedMeasurements);
  }, [weatherConstraints]);
  if (hide) return null;
  return (
    <Grid
      item
      container
      wrap='wrap'
      style={{ padding: '1rem', position: 'relative' }}
    >
      <Grid item container xs spacing={1} style={{ minWidth: '410px' }}>
        {weatherConstraints.map((constraint, idx) => (
          <Grid
            key={idx}
            item
            xs={3}
            wrap={'nowrap'}
            container
            alignItems='center'
            spacing={1}
            style={{ minWidth: '400px' }}
          >
            <Grid item>
              <FormControl variant='outlined'>
                <InputLabel id='measurement-select-label'>
                  Measurement
                </InputLabel>
                <Select
                  labelId='measurement-select-label'
                  id='measurement-select'
                  label='Measurement'
                  value={constraint.measurement}
                  onChange={(e) => {
                    if (Number(e.target.value) === 0) {
                      setWeatherConstraints(
                        // @ts-ignore
                        changeArrayAtIndex(
                          weatherConstraints,
                          {
                            ...constraint,
                            measurement: e.target.value,
                            level: '0',
                          },
                          idx
                        )
                      );
                    } else {
                      setWeatherConstraints(
                        // @ts-ignore
                        changeArrayAtIndex(
                          weatherConstraints,
                          {
                            ...constraint,
                            measurement: e.target.value,
                          },
                          idx
                        )
                      );
                    }
                  }}
                  placeholder='None'
                  style={{ width: '200px' }}
                >
                  <MenuItem value={0}>
                    <em>None</em>
                  </MenuItem>
                  {constraint.measurement && (
                    <MenuItem value={constraint.measurement}>
                      {
                        reverseWeatherMapWithExtras[
                          constraint.measurement as MeasurementData
                        ]
                      }
                    </MenuItem>
                  )}
                  {unusedMeasurements.map(
                    (measurementName: MeasurementWithExtras) => {
                      return (
                        <MenuItem
                          key={weatherMapWithExtras[measurementName].data}
                          value={weatherMapWithExtras[measurementName].data}
                        >
                          {measurementName}
                        </MenuItem>
                      );
                    }
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item style={{ paddingLeft: 0 }}>
              <IconButton
                onClick={() => {
                  setWeatherConstraints(
                    changeArrayAtIndex(
                      weatherConstraints,
                      {
                        ...constraint,
                        greaterThan: !constraint.greaterThan,
                      },
                      idx
                    )
                  );
                }}
                sx={{ width: '45px', height: '45px' }}
              >
                <Typography variant='h5'>
                  {constraint.greaterThan ? '>' : '<'}
                </Typography>
              </IconButton>
            </Grid>
            <Grid item style={{ paddingLeft: 0 }}>
              <TextField
                type='number'
                value={constraint.level}
                onChange={(e) => {
                  setWeatherConstraints(
                    changeArrayAtIndex(
                      weatherConstraints,
                      {
                        ...constraint,
                        level: e.target.value,
                      },
                      idx
                    )
                  );
                }}
                style={{ width: '100px' }}
                label={
                  'Limit ' +
                  (constraint.measurement
                    ? '(' +
                      getUnitForMeasurementType(
                        constraint.measurement as MeasurementData,
                        inImperial
                      ) +
                      ')'
                    : '')
                }
              >
                Limit
              </TextField>
            </Grid>
            <Grid item>
              {idx === weatherConstraints.length - 1 &&
              unusedMeasurements.length > 0 ? (
                <IconButton
                  onClick={() => {
                    setWeatherConstraints([
                      ...weatherConstraints,
                      {
                        measurement: '',
                        level: '0',
                        greaterThan: true,
                      },
                    ]);
                  }}
                >
                  <AddCircleOutline />
                </IconButton>
              ) : (
                <IconButton
                  onClick={() => {
                    setWeatherConstraints(
                      removeElementAtIndex(weatherConstraints, idx)
                    );
                  }}
                  disabled={
                    idx === weatherConstraints.length - 1 &&
                    unusedMeasurements.length > 0
                  }
                >
                  <DeleteOutline />
                </IconButton>
              )}
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};

export default WeatherReflectionConstraints;
